// library imports
import {
  Button,
  Card,
  Modal,
  Tooltip,
  theme,
  Tag,
  Flex,
  Select,
  Space,
  Typography,
} from "antd";
import { useState, useEffect, useContext, useReducer } from "react";
import dayjs from "dayjs";
import { SearchOutlined } from "@ant-design/icons";
// chart imports
import { Line } from "@ant-design/plots";
// component imports
import DataTable from "../common/DataTable";
// hook imports
import useFetch from "../../hooks/useFetch";
// context imports
import { IndexContext } from "../../context/IndexContext";
// config imports
import {
  DARK_BLUE,
  DARK_GREEN,
  DARK_THEME_DARK_BLUE,
  MQTT_REFRESH_RATE,
} from "../../configs/configs";

const { Text } = Typography;
const { useToken } = theme;

function reducer(state, action) {
  switch (action.type) {
    case "setStartDate":
      return { ...state, startDate: action.date };
    case "setEndDate":
      return { ...state, endDate: action.date };
    case "setDeviceSelected":
      return {
        ...state,
        deviceSelected: action.device,
      };
    default:
      return "Unrecognized command";
  }
}

function ActiveTable({ tableData, onViewClick, onDismissClick }) {
  const truncateText = (text, limit) => {
    const words = text.split("");
    const check = words.length > limit;
    return check ? words.slice(0, limit).join("") + "..." : text;
  };
  const activeTableColumns = [
    {
      title: "No.",
      dataIndex: "number",
      key: "number",
    },
    {
      title: "Device",
      dataIndex: "Device",
      key: "Device",
    },
    {
      title: "String No.",
      dataIndex: "StringNo",
      key: "StringNo",
    },
    {
      title: "Description",
      dataIndex: "Description",
      key: "Description",
      render: (text) => (
        <Tooltip title={text}>{truncateText(text, 20)}</Tooltip>
      ),
    },
    {
      title: "Time",
      dataIndex: "Time",
      key: "Time",
    },
    {
      title: "Action",
      dataIndex: "Action",
      key: "Action",
      render: (_, record, index) => (
        <div style={{ display: "flex", gap: "10px" }}>
          <Button
            onClick={() => {
              onViewClick(record);
            }}
          >
            View
          </Button>
          {/* <Button
            onClick={() => {
              onDismissClick(record);
            }}
          >
            Dismiss
          </Button> */}
        </div>
      ),
    },
  ];

  return <DataTable table_data={tableData} table_column={activeTableColumns} />;
}

function ActiveModal({
  modelOpen,
  modalClose,
  isLoading,
  title,
  chart_data,
  isLightTheme,
}) {
  const lineChartConfig = {
    autoFit: true,
    data: chart_data?.length ? chart_data : [],
    xField: "time",
    yField: "data",
    seriesField: "type",

    legend: {
      layout: "horizontal",
      position: "bottom",
    },
    point: false,
    xAxis: {
      title: {
        text: "Time",
        style: {
          fontSize: 16,
        },
      },
    },
    yAxis: {
      title: {
        text: "Parameter",
        style: {
          fontSize: 16,
        },
      },
      label: {
        formatter: (val) => parseFloat(val).toFixed(2),
      },
    },
    tooltip: {
      formatter: (datum) => ({
        name: datum.type,
        value: datum.data.toFixed(2), // 2 decimal places
      }),
    },
    color: isLightTheme
      ? ["grey", DARK_BLUE, DARK_GREEN]
      : ["grey", DARK_THEME_DARK_BLUE, DARK_GREEN],
  };

  return (
    <Modal
      open={modelOpen}
      onCancel={() => modalClose()}
      footer={null}
      style={{ maxWidth: "90vw", minWidth: "70vw", height: "80vh" }}
    >
      <Card
        title={title ? title : ""}
        style={{ marginTop: "30px" }}
        loading={isLoading}
      >
        <Line {...lineChartConfig} />
      </Card>
    </Modal>
  );
}

function ActivePvCondition() {
  // token
  const { token } = useToken();

  // fetch wrapper
  const [fetchWrapper] = useFetch();

  // load index context
  const { siteSelected, messageApi, isLightTheme, notificationApi } =
    useContext(IndexContext);

  // loading state
  const [isLoading, setIsLoading] = useState(false);

  // modal open state
  const [activeModal, setActiveModal] = useState(false);

  // table data
  const [activeTableData, setActiveTableData] = useState([]);

  // chart data
  const [activeChartData, setActiveChartData] = useState([]);

  // view record ID (to display correct modal title)
  const [viewRecordID, setViewRecordID] = useState(0);

  // device selection
  const [deviceList, setDeviceList] = useState([]);

  // active alarm filter
  const [activeAlarmFilter, setActiveAlarmFilter] = useState([]);

  // site commissioning date
  const [siteCommissioningDate, setSiteCommissioningDate] = useState(dayjs());

  const initialState = {
    deviceSelected: [],
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  // dispatch functions
  function handleDeviceChange(device) {
    dispatch({ type: "setDeviceSelected", device: device });
  }

  const onViewClick = (record) => {
    setActiveModal(true);
    setViewRecordID(record.ID);
    getChartData(record.ID);
  };

  const onModalClose = () => {
    setActiveModal(false);
  };

  const onDismissClick = (record) => {
    dismissActiveRecords(record.ID);
    getActiveRecords();
  };

  /* API to get site commissioning date */
  function getSiteCommissioningDate() {
    fetchWrapper({
      endpoint_url: "common/getSiteCommissioningDate",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setSiteCommissioningDate(response.data.site_commissioning_date);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get site commissioning date.",
          description: response.statusText,
        });
      },
    });
  }

  /* API to get device list*/
  function getDeviceList() {
    fetchWrapper({
      endpoint_url: "pv_condition/getDeviceList",
      method: "GET",
      set_content_type_header: false,
      params: { site_id: siteSelected },
      onSuccess: (response) => {
        setDeviceList(response.data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get device list.",
          description: response.statusText,
        });
        setDeviceList([]);
      },
    });
  }

  /** API to get active record */
  function getActiveRecords() {
    setIsLoading(true);

    fetchWrapper({
      endpoint_url: "pv_condition/getAllActive",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        device_id_list:
          activeAlarmFilter.length === 0
            ? deviceList.map((item) => item.value)
            : activeAlarmFilter,
      },
      onSuccess: (response) => {
        setActiveTableData(response.data);
      },
      onFinish: () => {
        setIsLoading(false);
      },
    });
  }

  /** API to get chart data */
  function getChartData(idx) {
    setIsLoading(true);

    fetchWrapper({
      endpoint_url: "pv_condition/getChartData",
      method: "POST",
      body: {
        record_id: idx,
      },
      onSuccess: (response) => {
        setActiveChartData(response.data);
      },
      onFinish: () => {
        setIsLoading(false);
      },
    });
  }

  /** API to dismiss active record */
  function dismissActiveRecords(idx) {
    setIsLoading(true);

    fetchWrapper({
      endpoint_url: "pv_condition/dismissActiveRecords",
      method: "POST",
      body: {
        record_id: idx,
      },
      onSuccess: (response) => {
        messageApi.open({
          type: "success",
          content: response.message,
        });
      },
      onFinish: () => {
        setIsLoading(false);
      },
    });
  }

  let modal_title = "";
  if (activeTableData?.length) {
    if (viewRecordID > 0) {
      const viewed_record = activeTableData.find(
        (element) => element.ID === viewRecordID
      );
      modal_title = viewed_record.Device + " - " + viewed_record.Parameter;
    }
  }

  useEffect(() => {
    if (siteSelected) {
      getDeviceList();
      getSiteCommissioningDate();
    }
  }, [siteSelected]);

  useEffect(() => {
    // call the API only when device list has been fetched
    if (deviceList.length > 0) {
      if (siteSelected) {
        getActiveRecords();

        // refresh data every configurable second
        const timer = setInterval(() => {
          getActiveRecords();
        }, MQTT_REFRESH_RATE * 1000);

        return () => {
          clearInterval(timer);
        };
      }
    }
  }, [deviceList, siteSelected, activeAlarmFilter]);

  return (
    <>
      <Flex
        className="mb-4"
        gap={8}
        wrap={true}
        align="flex-start"
        justify="space-between"
      >
        <Space.Compact block style={{ width: "fit-content" }}>
          <Button disabled>Device</Button>
          <Select
            placeholder={
              <Tag color={token.colorPrimary} className="pl-3 pr-3">
                <Text style={{ color: "white" }}>All</Text>
              </Tag>
            }
            style={{ width: "calc(100vw - 180px)" }}
            value={state.deviceSelected}
            options={deviceList}
            mode={"multiple"}
            allowClear
            tagRender={({ label, onClose, closable }) => (
              <Tag
                color={token.colorPrimary}
                closable={closable}
                onClose={onClose}
                style={{
                  marginRight: 3,
                }}
              >
                <Text style={{ color: "white" }}>{label}</Text>
              </Tag>
            )}
            onChange={(value) => {
              handleDeviceChange(value);
            }}
          />
        </Space.Compact>

        <Flex gap={8}>
          <Tooltip title="Search">
            <Button
              icon={<SearchOutlined />}
              onClick={() => {
                setActiveAlarmFilter(state.deviceSelected);
              }}
            />
          </Tooltip>
        </Flex>
      </Flex>

      <ActiveTable
        tableData={activeTableData}
        onViewClick={onViewClick}
        onDismissClick={onDismissClick}
      />
      <ActiveModal
        modelOpen={activeModal}
        modalClose={onModalClose}
        isLoading={isLoading}
        title={modal_title}
        chart_data={activeChartData}
        isLightTheme={isLightTheme}
      />
    </>
  );
}

export default ActivePvCondition;
